import type { ChannelOutput } from '../_shared/channel-types'
import type { BlockSpec } from './intent-to-blockspec'
import {
  backendRetrievalToComposePreview,
  backendV3ToChannelOutput,
  type BackendV3Response,
  type BackendV3SuccessResponse,
} from './backend-to-channel-output'
import { isCompleteBlockExample, type BlockExample } from './corpus-few-shot'

export interface RealStreamArgs {
  blockSpec: BlockSpec
  /** Endpoint URL of cogo-cloudflare's POST /api/v3/block/generate */
  backendUrl: string
  /** Pipeline mode for the backend; default 'v2' for Phase 1 (no ontology yet) */
  pipeline?: 'hybrid' | 'v2' | 'hybrid-with-fallback'
  /**
   * Corpus few-shot examples for `ontology.examples`. See corpus-few-shot.ts:
   * the route filters these by archetype and `renderFewShot` prints them for
   * all three stages. Incomplete entries are dropped here as a second gate —
   * an example missing a stage payload renders an EMPTY canonical example,
   * which is worse than sending none.
   */
  examples?: readonly BlockExample[]
  signal?: AbortSignal
  /** Replace the global fetch — for tests. */
  fetchImpl?: typeof fetch
  /** Inject Date.now for deterministic shape SHA / timing in tests. */
  nowMs?: () => number
}

/**
 * Fetch /api/v3/block/generate and yield exactly one ChannelOutput.
 *
 * Phase 1: synchronous request (no SSE). Mock-stream's `streaming` discriminant
 * is NOT emitted by real-mode here — the consumer should rely on
 * `chatRail.isLoading`-style indicators while this resolves. Streaming
 * progress is deferred to Phase 2 when /api/v2/app/generate SSE wires in.
 *
 * Three terminal outcomes:
 *   - `rendered`   — backend produced a valid bundle that passed quality gate
 *   - `shaclFail`  — backend's validation failed OR quality.grade = 'reject'
 *   - `miss`       — abort, network error, or non-2xx without validation report
 */
export async function* realStream(
  args: RealStreamArgs,
): AsyncIterable<ChannelOutput> {
  const fetchImpl = args.fetchImpl ?? globalThis.fetch
  const now = args.nowMs ?? (() => Date.now())
  const startMs = now()

  if (args.signal?.aborted) {
    yield { kind: 'miss', reason: 'aborted' }
    return
  }

  // CanonicalFacts are still empty — the hybrid prompt builders fall back to
  // their built-in vocabulary when `componentTypes` is empty, so this is a
  // degraded but working prompt, not a broken one. `examples` is the field
  // this request actually carries: it is the ONLY one of the eight the route
  // reads back out (as `getExamples`) and the prompts render.
  //
  // `pipeline: 'v2'` returns from the route BEFORE `getExamples` is even
  // synthesized (block-v3.ts's 'v2' branch calls generateBlockV2 directly), so
  // examples sent under 'v2' are inert. A request that carries examples is
  // therefore escalated to hybrid by `dispatch.ts`; the default here stays
  // 'v2' so an example-less request is byte-identical to before.
  const examples = (args.examples ?? []).filter((example) => isCompleteBlockExample(example))
  const body = {
    blockSpec: args.blockSpec,
    pipeline: args.pipeline ?? 'v2',
    ontology: {
      prefixes: [],
      accessors: [],
      builtinActions: [],
      componentTypes: [],
      lifecycleHooks: [],
      antiPatterns: [],
      ontologyVersion: {},
      examples,
    },
  }

  let httpRes: Response
  try {
    httpRes = await fetchImpl(args.backendUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: args.signal,
    })
  } catch (e) {
    // AbortError: surface as miss/aborted; other network errors as backend-failed.
    if (
      e instanceof Error &&
      (e.name === 'AbortError' || e.message.includes('aborted'))
    ) {
      yield { kind: 'miss', reason: 'aborted' }
      return
    }
    yield {
      kind: 'miss',
      reason: 'backend-failed',
      details: `network error: ${e instanceof Error ? e.message : String(e)}`,
    }
    return
  }

  let parsed: BackendV3Response
  try {
    parsed = (await httpRes.json()) as BackendV3Response
  } catch (e) {
    yield {
      kind: 'miss',
      reason: 'backend-failed',
      details: `invalid JSON response: ${e instanceof Error ? e.message : String(e)}`,
    }
    return
  }

  const terminal = backendV3ToChannelOutput(
    { ok: httpRes.ok, status: httpRes.status, body: parsed },
    args.blockSpec,
    { startMs },
  )
  // Compose provenance card ("why this block was selected") — emitted before
  // the render only when the backend reported a real retrieve+bind pick trace.
  // Never synthesized client-side: absent/zero-shot/older-backend → no card.
  if (terminal.kind === 'rendered' && httpRes.ok) {
    const preview = backendRetrievalToComposePreview(
      parsed as BackendV3SuccessResponse,
      args.blockSpec,
    )
    if (preview) yield preview
  }
  yield terminal
}
